import {
  GridToolbar,
  GridColumnOrderChangeParams,
  GridFilterPanel,
  GridFilterModel,
  useGridApiContext,
  GridColDef,
  GridLogicOperator,
  gridFilterModelSelector,
  useGridSelector
} from '@mui/x-data-grid-pro';
import { LicenseInfo } from '@mui/x-license-pro';
import React, { useEffect, useMemo, useState } from 'react';

import { DataGridProps } from './DataGrid.props';
import { FilterWithSearchButtonWrapper, FilterWithSearchWrapper, GridFlexGrowWrapper, GridFlexWrapper, StyledDataGrid } from './DataGrid.styles';
import { GridDensityTypes } from '../../../configs/enums';
import { CUSTOM_DATE_FILTER_OPERATORS, CUSTOM_DATE_FILTER_OPERATORS_WITHOUT_RELATIVE_TIMES } from '../../../constants/dateFilterOperators';
import { checkListPagetFilterAvailability, hasRelativeTimesDatePickerFilter } from '../../../utils/common';
import { Button, ButtonColorProps, ButtonVariantProps } from '../Button';

LicenseInfo.setLicenseKey('fe7199317704efcff308c1e42d6576faTz0xMTExNTgsRT0xNzc1ODY1NTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=');

// replace emptly element with DataGrid footer if footer disabled
export function CustomFooterStatusComponent() {
  return (
    <></>
  );
}

export const DataGrid: React.FC<DataGridProps> = ({
  columns,
  rows,
  density = GridDensityTypes.Compact,
  getRowId,
  componentsProps,
  columnVisibilityModel,
  disableColumnFilter,
  disableColumnMenu,
  disableColumnSelector,
  disableDensitySelector,
  checkboxSelection,
  getDetailPanelHeight,
  getDetailPanelContent,
  filterMode,
  onFilterModelChange,
  loading,
  rowCount,
  rowsPerPageOptions,
  pagination,
  page,
  pageSize,
  paginationMode,
  onPageChange,
  onPageSizeChange,
  customToolbar,
  filterModel,
  onSortModelChange,
  sortingMode,
  getRowClassName,
  selectionModel,
  onSelectionModelChange,
  hideFooter,
  autoHeight = false,
  treeData,
  getTreeDataPath,
  defaultGroupingExpansionDepth,
  groupingColDef,
  initialState,
  getRowHeight,
  getEstimatedRowHeight = () => 100, // Approximate average row height
  isRowSelectable,
  setColumns,
  customFooter,
  onCellClick,
  onCellEditStop,
  apiRef,
  experimentalFeatures,
  getCellClassName,
  showCellVerticalBorder,
  headerHeight,
  onCellDoubleClick,
  isGroupExpandedByDefault,
  serviceColumns,
  disableSelectionOnClick,
  hasFilterPanelWithSearch = true,
  filtersSearch,
  hideFooterComponent,
  defaultReport,
  id,
  pageName
}) => {
  const storageKey = id || '';
  const [updatedColumns, setUpdatedColumns] = useState<GridColDef[]>([]);
  const [customFilterModel, setCustomFilterModel] = useState<GridFilterModel>({ items: [] });
  const [filtersAppliedOnLoad, setFiltersAppliedOnLoad] = useState(false);

  useEffect(() => {
    setUpdatedColumns(customizeDateColumns(columns));
  }, [columns]);

  useEffect(() => {
    if (defaultReport) {
      setCustomFilterModel({ items: [] });
      storageKey && sessionStorage.removeItem(storageKey);
    }
  }, [defaultReport, storageKey]);

  // For report filters or default filters in list pages
  useEffect(() => {
    if (hasFilterPanelWithSearch && filterModel?.items?.length) {
      const filters = filterModel.items.map((filter: any) => ({
        id: filter?.id,
        field: filter?.columnField || filter?.field,
        operator: filter?.operatorValue || filter?.operator,
        value: filter?.value
      }));

      if (!storageKey) {
        setCustomFilterModel({ items: filters });
        return;
      }

      const stored = sessionStorage.getItem(storageKey);
      const parsed = stored ? JSON.parse(stored) : null;
      const hasStoredItems = !!parsed?.items?.length;

      if (hasStoredItems) {
        setCustomFilterModel(parsed);
      } else {
        const newModel = { items: filters };
        setCustomFilterModel(newModel);
        sessionStorage.setItem(storageKey, JSON.stringify(newModel));
      }
    }
  }, [filterModel, storageKey]);

  useEffect(() => {
    if (!filtersAppliedOnLoad && storageKey && hasFilterPanelWithSearch && updatedColumns.length > 0) {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.items?.length) {
          setCustomFilterModel(parsed);
          onFilterModelChange?.(parsed);
          filtersSearch?.();
          setFiltersAppliedOnLoad(true);
        }
      }
    }
  }, [filtersAppliedOnLoad, storageKey, hasFilterPanelWithSearch, updatedColumns]);

  const FilterPanelWithSearch = (props: any) => {
    const apiRef = useGridApiContext();
    const currentFilterModel = useGridSelector(apiRef, gridFilterModelSelector);

    const handleSearch = () => {
      onFilterModelChange?.(currentFilterModel);
      filtersSearch?.(); // To refetch list query when Search is clicked with no filter changes
      apiRef.current.hideFilterPanel();
    };

    const handleReset = () => {
      // Remove saved filters
      storageKey && sessionStorage.removeItem(storageKey);

      // Reset to empty filter model (no filters)
      const defaultFilters = { items: [] };

      // Update state so DataGrid reflects reset
      onFilterModelChange?.(defaultFilters);
      setCustomFilterModel(defaultFilters);

      // Close filter panel
      apiRef.current.hideFilterPanel();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && currentFilterModel.items.every((filter) => filter.operator !== 'isAnyOf')) {
        event.preventDefault();
        handleSearch();
      }
    };

    return (
      <FilterWithSearchWrapper>
        <GridFilterPanel
          {...props}
          key="custom-filter-panel"
          logicOperators={[GridLogicOperator.And]}
          columnsSort="asc"
          disableRemoveAllButton
          onKeyDown={handleKeyDown}
          filterFormProps={{
            // Customize inputs by passing props
            linkOperatorInputProps: {
              sx: { display: 'none' }
            },
            columnInputProps: {
              variant: 'outlined',
              size: 'small',
              sx: { mt: 'auto' },
            },
            operatorInputProps: {
              variant: 'outlined',
              size: 'small',
              sx: { mt: 'auto' },
            },
            valueInputProps: {
              InputComponentProps: {
                variant: 'outlined',
                size: 'small',
              },
              sx: { width: 'auto', minWidth: '200px' }
            },
            deleteIconProps: {
              sx: {
                '& .MuiSvgIcon-root': { color: '#d32f2f' },
              },
            }
          }}
        />
        <FilterWithSearchButtonWrapper>
          <Button
            variant={ButtonVariantProps.Secondary}
            color={ButtonColorProps.Primary}
            disabled={!checkListPagetFilterAvailability(currentFilterModel) || currentFilterModel?.items?.length === 0}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button
            variant={ButtonVariantProps.Secondary}
            color={ButtonColorProps.Primary}
            disabled={!checkListPagetFilterAvailability(currentFilterModel) || currentFilterModel?.items?.length === 0}
            onClick={handleSearch}
          >
            Search
          </Button>
        </FilterWithSearchButtonWrapper>
      </FilterWithSearchWrapper>
    );
  };

  const handleFilterModelChange = (updatedFilterModel: GridFilterModel) => {
    if (hasFilterPanelWithSearch) {
      const isDifferent = JSON.stringify(updatedFilterModel) !== JSON.stringify(customFilterModel);

      if (isDifferent) {
        setCustomFilterModel(updatedFilterModel);
        if (storageKey) {
          sessionStorage.setItem(storageKey, JSON.stringify(updatedFilterModel));
        }
      }
    } else if (onFilterModelChange) {
      onFilterModelChange(updatedFilterModel);
      if (storageKey) {
        sessionStorage.setItem(storageKey, JSON.stringify(updatedFilterModel));
      }
    }
  };


  const customizeDateColumns = (columns: GridColDef[]) => {
    const updatedColumns = columns.map((column) =>
      column.type === 'date' ?
        hasRelativeTimesDatePickerFilter(pageName as string, column.field) ?
          { ...column, filterOperators: CUSTOM_DATE_FILTER_OPERATORS } :
          { ...column, filterOperators: CUSTOM_DATE_FILTER_OPERATORS_WITHOUT_RELATIVE_TIMES } :
        column);
    return updatedColumns;
  };

  return (
    <GridFlexWrapper>
      <GridFlexGrowWrapper>
        <StyledDataGrid
          columns={updatedColumns}
          rows={rows}
          density={density}
          getRowId={getRowId}
          getRowHeight={getRowHeight}
          getEstimatedRowHeight={getEstimatedRowHeight}
          disableColumnFilter={disableColumnFilter}
          disableColumnMenu={disableColumnMenu}
          disableColumnSelector={disableColumnSelector}
          disableDensitySelector={disableDensitySelector}
          disableRowSelectionOnClick={disableSelectionOnClick}
          checkboxSelection={checkboxSelection}
          getDetailPanelHeight={getDetailPanelHeight}
          getDetailPanelContent={getDetailPanelContent}
          initialState={initialState}
          sx={{
            '--DataGrid-containerBackground': 'white'
          }}
          slotProps={{
            ...componentsProps,
            filterPanel: {
              // Force usage of "And" operator
              logicOperators: [GridLogicOperator.And],
              // Display columns by ascending alphabetical order
              columnsSort: 'asc',
              filterFormProps: {
                // Customize inputs by passing props
                logicOperatorInputProps: {
                  sx: { display: 'none' }
                },
                columnInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                operatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                valueInputProps: {
                  InputComponentProps: {
                    variant: 'outlined',
                    size: 'small',
                  },
                  sx: { width: 'auto', minWidth: '200px' }
                },
                deleteIconProps: {
                  sx: {
                    '& .MuiSvgIcon-root': { color: '#d32f2f' },
                  },
                },
              }
            },
          }}
          columnVisibilityModel={columnVisibilityModel}
          filterMode={filterMode}
          onFilterModelChange={handleFilterModelChange}
          loading={loading}
          rowCount={rowCount || 0}
          pageSizeOptions={rowsPerPageOptions}
          pagination={pagination}
          paginationModel={{ page: page as number, pageSize: pageSize as number }}
          paginationMode={paginationMode}
          onPaginationModelChange={({ page, pageSize }) => {
            onPageChange && onPageChange(page);
            onPageSizeChange && onPageSizeChange(pageSize);
          }}
          onSortModelChange={onSortModelChange}
          sortingMode={sortingMode}
          slots={useMemo(() => ({
            toolbar: customToolbar || GridToolbar,
            ...(hideFooter && { footer: customFooter || CustomFooterStatusComponent }),
            ...(hasFilterPanelWithSearch && { filterPanel: FilterPanelWithSearch })
          }), [customToolbar, hideFooter, customFooter, hasFilterPanelWithSearch])}
          filterModel={hasFilterPanelWithSearch ? customFilterModel : filterModel}
          getRowClassName={getRowClassName}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={onSelectionModelChange}
          autoHeight={autoHeight}
          treeData={treeData}
          getTreeDataPath={getTreeDataPath}
          defaultGroupingExpansionDepth={defaultGroupingExpansionDepth}
          groupingColDef={groupingColDef}
          isRowSelectable={isRowSelectable}
          disableVirtualization={true}
          onColumnOrderChange={(params: GridColumnOrderChangeParams) => {
            const { targetIndex, oldIndex } = params;
            const columnsSet = serviceColumns || columns;
            const updatedColumns = [...columnsSet];
        
            // Remove the column from the old index
            const [movedColumn] = updatedColumns.splice(oldIndex, 1);
        
            // Insert the column at the target index
            updatedColumns.splice(targetIndex, 0, movedColumn);

            setColumns && setColumns(updatedColumns);
          }}
          onCellClick={onCellClick}
          onCellEditStop={onCellEditStop}
          apiRef={apiRef}
          experimentalFeatures={experimentalFeatures}
          getCellClassName={getCellClassName}
          showCellVerticalBorder={showCellVerticalBorder}
          columnHeaderHeight={headerHeight || 56}
          onCellDoubleClick={onCellDoubleClick}
          isGroupExpandedByDefault={isGroupExpandedByDefault}
          hideFooter={hideFooterComponent}
          onColumnWidthChange={(params) => {
            const columnsSet = serviceColumns || columns;
            const updatedColumns = columnsSet.map((column) => {
              if (column?.field === params?.colDef?.field) {
                column.width = params.width;
              }
              return column;
            });
            setColumns && setColumns(updatedColumns);
          }}
        />
      </GridFlexGrowWrapper>
    </GridFlexWrapper>
  );
};